import { getApiClient } from "@src/api";
import {
	invalidateNavbarBranchSnapshots,
	optimisticallyRemoveNavbarItem,
	restoreNavbarBranchSnapshots,
} from "@src/components/board/mutations/navbar-optimistic";
import { parseWhiteboardDocumentId } from "@src/components/collaboration/document-id";
import { graphql } from "@src/gql/gql";
import { handleError } from "@src/utils/error-handling";
import { showSuccessNotification } from "@src/utils/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const leaveWhiteboardMutation = graphql(/* GraphQL */ `
	mutation leaveWhiteboard($whiteboardId: String!) {
		whiteboards {
			leave(whiteboardId: $whiteboardId) {
				causationId
				commandId
				correlationId
				id
				name
				ownerId
				status
			}
		}
	}
`);

/**
 * Leave a shared whiteboard (Shared With Me). GraphQL: `whiteboards.leave`.
 */
export function useLeaveWhiteboard(showNotification = true) {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (whiteboardId: string) => {
			const result = await client.mutate({
				mutation: leaveWhiteboardMutation,
				variables: {
					whiteboardId: parseWhiteboardDocumentId(whiteboardId).toIdString(),
				},
			});

			if (result.errors && result.errors.length > 0) {
				throw new Error(
					result.errors[0]?.message ?? "Failed to leave whiteboard",
				);
			}

			return result.data;
		},
		onMutate: async (whiteboardId) => {
			const snapshots = await optimisticallyRemoveNavbarItem(
				queryClient,
				whiteboardId,
			);
			return { snapshots };
		},
		onSuccess: () => {
			if (showNotification) {
				showSuccessNotification("Success", "Left successfully");
			}
		},
		onError: (error, _vars, context) => {
			if (context?.snapshots) {
				restoreNavbarBranchSnapshots(queryClient, context.snapshots);
			}
			handleError(error, "Failed to leave whiteboard");
		},
		onSettled: (_data, _error, _vars, context) => {
			invalidateNavbarBranchSnapshots(queryClient, context?.snapshots ?? []);
		},
	});
}
